/**
 * @fileoverview PantoneColorPicker - Modal component for selecting Pantone colors
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  FormGroup,
  Label,
} from 'reactstrap';
import { SketchPicker } from 'react-color';
import { PANTONE_COLORS, searchPantoneColors, type PantoneColor } from '../data/pantoneColors';

interface PantoneColorPickerProps {
  isOpen: boolean;
  onColorSelect: (color: PantoneColor) => void;
  onClose: () => void;
  selectedColor?: PantoneColor;
}

export const PantoneColorPicker: React.FC<PantoneColorPickerProps> = ({
  isOpen,
  onColorSelect,
  onClose,
  selectedColor,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [allColors, setAllColors] = useState<PantoneColor[]>(PANTONE_COLORS);
  const [filteredColors, setFilteredColors] = useState<PantoneColor[]>(PANTONE_COLORS);
  const [useFullCatalog, setUseFullCatalog] = useState(false);
  const [currentSelectedColor, setCurrentSelectedColor] = useState<PantoneColor | null>(selectedColor || null);
  const [colorPickerColor, setColorPickerColor] = useState('#000000');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  // categories computed below from allColors

  // Lazy-load full catalog on open
  useEffect(() => {
    const loadFull = async () => {
      try {
        const res = await fetch('/pantone-full.parsed.json', { cache: 'force-cache' });
        if (res.ok) {
          const json = await res.json();
          if (Array.isArray(json) && json.length > PANTONE_COLORS.length) {
            setAllColors(json);
            setUseFullCatalog(true);
          }
        }
      } catch (_) {
        // fallback silently to bundled subset
      }
    };
    if (isOpen) {
      loadFull();
    }
  }, [isOpen]);

  // Filter colors based on search term and category
  useEffect(() => {
    let filtered: PantoneColor[] = allColors;

    // Filter by search term
    if (searchTerm.trim() !== '') {
      filtered = searchPantoneColors(searchTerm);
    }

    // Filter by category
    if (selectedCategory) {
      filtered = filtered.filter((color) => color.category === selectedCategory);
    }

    setFilteredColors(filtered);
  }, [searchTerm, selectedCategory, allColors]);

  // Recompute categories when dataset changes (optional UI usage)
  const categories = React.useMemo(() => {
    const set = new Set<string>();
    allColors.forEach(c => { if (c.category) set.add(c.category); });
    return Array.from(set).sort();
  }, [allColors]);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Handle color selection from list
  const handleColorSelect = (color: PantoneColor) => {
    setCurrentSelectedColor(color);
    setColorPickerColor(color.hex);
  };

  // Handle color picker change
  const handleColorPickerChange = (color: any) => {
    setColorPickerColor(color.hex);
    // Create a hex-only color object (remove "Custom" prefix)
    const customColor: PantoneColor = {
      name: `${color.hex}`,
      hex: color.hex,
      pantone: `${color.hex}`,
    };
    setCurrentSelectedColor(customColor);
  };

  // Handle confirm selection
  const handleConfirmSelection = () => {
    if (currentSelectedColor) {
      onColorSelect(currentSelectedColor);
      onClose();
    }
  };

  // Handle modal close
  const handleClose = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setCurrentSelectedColor(selectedColor || null);
    setShowColorPicker(false);
    onClose();
  };

  // Handle click outside modal
  const handleModalClick = (e: React.MouseEvent) => {
    if (e.target === modalRef.current) {
      handleClose();
    }
  };

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setSelectedCategory('');
      setCurrentSelectedColor(selectedColor || null);
      setShowColorPicker(false);
      if (selectedColor) {
        setColorPickerColor(selectedColor.hex);
      }
    }
  }, [isOpen, selectedColor]);

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      size="lg"
      centered
      backdrop="static"
      keyboard={false}
      data-testid="pantone-color-picker"
      role="dialog"
      aria-labelledby="pantone-color-picker-title"
      aria-describedby="pantone-color-picker-description"
    >
      <div ref={modalRef} onClick={handleModalClick}>
        <ModalHeader toggle={handleClose}>
          <h5 id="pantone-color-picker-title" className="modal-title">
            <i className="fas fa-palette me-2" aria-hidden="true"></i>
            Selector de Colores Pantone
          </h5>
        </ModalHeader>
        
        <ModalBody>
          <div id="pantone-color-picker-description" className="sr-only">
            Selecciona un color Pantone usando el buscador, el selector de color o la lista de colores disponibles.
          </div>
          <Row>
            {/* Search Section */}
            <Col md={8} className="mb-3">
              <div className="position-relative">
                <Input
                  type="text"
                  placeholder="Buscar color Pantone..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="form-control"
                  aria-label="Buscar color Pantone"
                  aria-describedby="search-help"
                />
                <i className="fas fa-search position-absolute end-0 top-50 translate-middle-y me-3 text-muted"></i>
              </div>
            </Col>
            
            {/* Category Filter */}
            <Col md={4} className="mb-3">
              <FormGroup>
                <Label for="category-filter" className="form-label">Categoría</Label>
                <Input
                  type="select"
                  id="category-filter"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="form-control"
                  aria-label="Filtrar por categoría"
                >
                  <option value="">Todas las categorías</option>
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>

            {/* Color Picker Section */}
            <Col md={6} className="mb-3">
              <Card>
                <CardBody>
                  <h6 className="card-title">
                    <i className="fas fa-eye-dropper me-2"></i>
                    Selector de Color
                  </h6>
                  
                  <div className="text-center mb-3">
                    <div
                      className="mx-auto border rounded"
                      style={{
                        width: '100px',
                        height: '100px',
                        backgroundColor: colorPickerColor,
                        cursor: 'pointer',
                      }}
                      onClick={() => setShowColorPicker(!showColorPicker)}
                    />
                    <small className="text-muted d-block mt-2">
                      {colorPickerColor}
                    </small>
                  </div>

                  {showColorPicker && (
                    <div className="d-flex justify-content-center">
                      <SketchPicker
                        color={colorPickerColor}
                        onChange={handleColorPickerChange}
                        data-testid="sketch-picker"
                      />
                    </div>
                  )}

                  <Button
                    color="outline-primary"
                    size="sm"
                    className="w-100"
                    onClick={() => setShowColorPicker(!showColorPicker)}
                  >
                    {showColorPicker ? 'Ocultar Selector' : 'Mostrar Selector'}
                  </Button>
                </CardBody>
              </Card>
            </Col>

            {/* Color List Section */}
            <Col md={6} className="mb-3">
              <Card>
                <CardBody>
                  <h6 className="card-title">
                    <i className="fas fa-list me-2"></i>
                    Colores Pantone
                  </h6>
                  
                  <div
                    className="color-list"
                    style={{ maxHeight: '400px', overflowY: 'auto' }}
                    data-testid="color-list"
                  >
                    {filteredColors.length === 0 ? (
                      <div className="text-center text-muted py-4">
                        <i className="fas fa-search fa-2x mb-2"></i>
                        <p>No se encontraron colores</p>
                      </div>
                    ) : (
                      <div className="row g-2">
                        {filteredColors.map((color, index) => (
                          <div key={index} className="col-6">
                            <div
                              className={`color-item p-2 border rounded cursor-pointer ${
                                currentSelectedColor?.hex === color.hex ? 'selected border-primary' : ''
                              }`}
                              onClick={() => handleColorSelect(color)}
                              style={{ cursor: 'pointer' }}
                              role="button"
                              tabIndex={0}
                              aria-label={`Seleccionar color ${color.name}`}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleColorSelect(color);
                                }
                              }}
                            >
                              <div className="d-flex align-items-center">
                                <div
                                  className="color-swatch me-2 border rounded"
                                  style={{
                                    width: '20px',
                                    height: '20px',
                                    backgroundColor: color.hex,
                                  }}
                                />
                                <div className="flex-grow-1">
                                  <small className="fw-bold d-block">{color.name}</small>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Selected Color Display */}
          {currentSelectedColor && (
            <Row>
              <Col md={12}>
                <Card className="bg-light">
                  <CardBody>
                    <h6 className="card-title">
                      <i className="fas fa-check-circle me-2 text-success"></i>
                      Color Seleccionado
                    </h6>
                    <div className="d-flex align-items-center">
                      <div
                        className="color-swatch me-3 border rounded"
                        style={{
                          width: '40px',
                          height: '40px',
                          backgroundColor: currentSelectedColor.hex,
                        }}
                      />
                      <div>
                        <div className="fw-bold">{currentSelectedColor.name}</div>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </ModalBody>

        <ModalFooter>
          <Button color="secondary" onClick={handleClose}>
            <i className="fas fa-times me-2"></i>
            Cancelar
          </Button>
          <Button
            color="primary"
            onClick={handleConfirmSelection}
            disabled={!currentSelectedColor}
          >
            <i className="fas fa-check me-2"></i>
            Seleccionar
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
};
